import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import ExportIcon from "../../../assets/Export_icon.svg";
import { Box, Grid, Paper, Typography, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { getTodaySalesSummary } from "../../../Services/authService";
import SectionLoader from "../../../Components/Common/SectionLoader";

export default function SalesLineChart() {
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const borderColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  const [view, setView] = useState("day");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open1, setOpen1] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // True only when there is at least one non-zero sales value
  const hasSalesData = data.length > 0 && data.some((d) => Number(d.value) > 0);

  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;

  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const colorSuccessExport =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;

  const settingIconColor =
    mode === "dark" ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;

  // Fetch sales data based on selected view
  const fetchSalesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTodaySalesSummary(view);
      setData(response || []);
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen1(false);
  };

  const handleExport = () => {
    if (!data || data.length === 0 || !data.some((d) => Number(d.value) > 0)) {
      setToastMessage(t("sales_analytics.noDataToExport"));
      setOpen1(true);
      return;
    }

    try {
      const rows = data.map((item) => ({
        [t("dashboard.period", "Period")]: item.day || "",
        [t("dashboard.sales", "Sales(€)")]: item.value ?? 0,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Total Sales");

      const buf = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;

      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);

      const fileNameMap = {
        week: t("sales_analytics.weekSalesFileName"),
        day: t("sales_analytics.daySalesFileName"),
        month: t("sales_analytics.monthSalesFileName"),
        year: t("sales_analytics.yearSalesFileName"),
      };

      const fileName =
        fileNameMap[view] || t("sales_analytics.totalSalesFileName");

      a.download = `${fileName}_${ts}.xlsx`;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting total sales:", error);

      setToastMessage(t("dashboard.exportFailed", "Error exporting data"));
      setOpen1(true);
    }
  };
  // Fetch data on component mount and when view changes
  useEffect(() => {
    fetchSalesData();
  }, [view]);

  // Handle view change
  const handleViewChange = (viewType) => {
    setView(viewType);
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      let translatedLabel = label;

      if (view === "week") {
        translatedLabel =
          t(`dashboard.${label}`) !== `dashboard.${label}`
            ? t(`dashboard.${label}`)
            : label;
      } else if (view === "month") {
        const [weekText, weekNumber] = label.split(" ");
        translatedLabel = `${t("dashboard.week")} ${weekNumber}`;
      } else if (view === "year") {
        translatedLabel =
          t(`dashboard.${label}`) !== `dashboard.${label}`
            ? t(`dashboard.${label}`)
            : label;
      }

      return (
        <Paper
          elevation={3}
          sx={{
            padding: "8px 12px",
            backgroundColor: "#2a2a2a",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {translatedLabel}
          </Typography>

          <Typography variant="body2">{`€ ${payload[0].value}`}</Typography>
        </Paper>
      );
    }
    return null;
  };
  const viewOptions = [
    { key: "day", labelKey: "dashboard.day" },
    { key: "week", labelKey: "dashboard.week" },
    { key: "month", labelKey: "dashboard.month" },
    { key: "year", labelKey: "dashboard.year" },
  ];
  const formatTickLabel = (tick) => {
    if (view === "week") {
      return t(`dashboard.${tick}`) || tick;
    }

    if (view === "month") {
      // "Week 1" → "1"
      const [, number] = tick.split(" ");
      return number;
    }

    if (view === "year") {
      return t(`dashboard.${tick}`) || tick;
    }

    if (view === "day") {
      return tick.slice(0, 2);
    }

    return tick;
  };

  return (
    <Box>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <h3 className="subheading-sec-card" style={{ margin: "0px" }}>
          {t("sales_analytics.title")}
        </h3>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Box
            className="line-days-weeks"
            sx={{
              display: "flex",
              gap: 6,
              cursor: "pointer",
            }}
          >
            {viewOptions.map((option) => (
              <Typography
                key={option.key}
                onClick={() => handleViewChange(option.key)}
                sx={{
                  borderBottom:
                    view === option.key ? `2px solid ${borderColor1}` : "none",
                }}
              >
                {t(option.labelKey)}
              </Typography>
            ))}
          </Box>

          {/* Export */}
          <Box
            onClick={handleExport}
            sx={{
              background: colorSuccessExport,
              color: settingIconColor,
              padding: "6px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            <img
              src={ExportIcon}
              alt="Export"
              style={{
                width: "12px",
                height: "12px",
              }}
            />
          </Box>
        </Box>
      </Grid>

      <Box
        sx={{
          overflowY: "auto",
          overflowY: "hidden",

          "&::-webkit-scrollbar": {
            width: "10px !important",
            height: "8px !important",
            padding: "2px !important",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scroll1,
            borderRadius: "10px",
            border: `1px solid ${scroll}`,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scroll,
            borderRadius: "10px",
            width: "3px !important",

            border: "3px solid transparent",
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: scroll,
            },
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "450px",
            position: "relative",
            "@media (max-width: 650px)": {
              minWidth: "1500px",
            },
          }}
        >
          {" "}
          {loading ? (
            <SectionLoader />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <>
              <ResponsiveContainer>
                <AreaChart
                  data={data}
                  margin={{ top: 30, right: 10, left: 0, bottom: 30 }}
                >
                  {/* Gradient Definition */}
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4a90e2" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#4a90e2" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  {/* X-Axis */}
                  <XAxis
                    dataKey="day"
                    stroke="#aaa"
                    tickLine={false}
                    axisLine={{ stroke: "#aaa" }}
                    padding={{ left: 10 }}
                    tickFormatter={formatTickLabel}
                    label={
                      view === "day"
                        ? {
                            value: t("dashboard.hours"),
                            position: "insideBottom",
                            offset: -13,
                          }
                        : view === "week"
                          ? {
                              value: t("dashboard.day"),
                              position: "insideBottom",
                              offset: -13,
                            }
                          : view === "month"
                            ? {
                                value: t("dashboard.week"),
                                position: "insideBottom",
                                offset: -13,
                              }
                            : view === "year"
                              ? {
                                  value: t("dashboard.month"),
                                  position: "insideBottom",
                                  offset: -13,
                                }
                              : undefined
                    }
                  />
                  {/* Y-Axis: Show ticks but hide lines */}
                  <YAxis
                    stroke="#aaa"
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => value}
                    padding={{ bottom: 10 }}
                    width={80}
                    domain={hasSalesData ? [0, "auto"] : [0, 5]}
                    tickCount={hasSalesData ? undefined : 6}
                    label={{
                      value: "€",
                      position: "insideLeft",
                      offset: 15,
                      style: {
                        fill: "#aaa",
                        fontSize: 16,
                        fontWeight: 600,
                      },
                    }}
                  />
                  {/* Custom Tooltip */}
                  <Tooltip content={<CustomTooltip />} />
                  {hasSalesData && (
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#5E5CE6"
                      strokeWidth={2}
                      fill="url(#gradient)"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {!hasSalesData && (
                <Typography
                  color="gray"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  {t("dashboard.noSalesDataForPeriod")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
      {/* Snackbar component */}
      <Snackbar
        open={open1}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
